import React, { useEffect, useState } from 'react';

interface GameCompassProps {
  playerPosition: { x: number; y: number };
  destination: { x: number; y: number; name?: string } | null;
  onClear: () => void;
}

export const GameCompass: React.FC<GameCompassProps> = ({ playerPosition, destination, onClear }) => {
  const [angle, setAngle] = useState<number>(0);
  const [distance, setDistance] = useState<number | null>(null);
  const [isNearby, setIsNearby] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (!destination) {
      setIsInitialized(false);
      setDistance(null);
      setAngle(0);
      setIsNearby(false);
      return;
    }

    // Skip calculation if player position is at origin (not yet synced)
    if (playerPosition.x === 0 && playerPosition.y === 0) {
      return;
    }

    // Calculate angle from player to destination
    const dx = destination.x - playerPosition.x;
    const dy = destination.y - playerPosition.y;
    const angleRad = Math.atan2(dy, dx);
    const angleDeg = (angleRad * 180) / Math.PI + 90; // +90 to make 0° point up
    
    setAngle(angleDeg);
    
    // Calculate distance
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    // Only update distance if it's reasonable (not a huge jump from unsynced position)
    // or if we haven't initialized yet
    if (!isInitialized || distance === null || Math.abs(dist - distance) < 1000) {
      setDistance(dist);
      setIsInitialized(true);
    }
    
    // Check if nearby (within 100 units)
    setIsNearby(dist < 100);
  }, [playerPosition, destination, isInitialized, distance]);

  const formatDistance = (dist: number): string => {
    // Convert pixels to tiles (16px per tile) - 'T' is our unit of measurement
    const tiles = Math.round(dist / 16);
    if (tiles < 5) return 'Nearby!';
    if (tiles < 100) return `${tiles}T`;
    return `${(tiles / 100).toFixed(1)}kT`;
  };

  const displayName = destination?.name?.trim() || '';
  const displayDistance = destination && distance !== null ? formatDistance(distance) : '';
  const isActive = Boolean(destination);

  return (
    <div className={`game-compass-container ${isActive ? 'active' : 'inactive'}`}>
      {/* Glass orb effect */}
      <div className={`game-compass-orb ${isNearby && isActive ? 'nearby' : ''}`}>
        {/* Compass needle */}
        <div 
          className="game-compass-needle"
          style={{ transform: `rotate(${angle}deg)` }}
        >
          <div className={`needle-point ${isActive ? 'active' : ''}`} />
          <div className="needle-tail" />
        </div>
        
        {/* Center dot */}
        <div className="game-compass-center" />
        
        {/* Cardinal markers */}
        <div className="cardinal-markers">
          <span className="cardinal n">N</span>
          <span className="cardinal e">E</span>
          <span className="cardinal s">S</span>
          <span className="cardinal w">W</span>
        </div>
      </div>
      
      {/* Destination info */}
      {isActive && displayName && (
        <div className="game-compass-info">
          <div className="destination-name">
            {displayName}
          </div>
          <div className="destination-distance">
            {displayDistance}
          </div>
        </div>
      )}
      
      {/* Clear button */}
      {destination && (
        <button className="game-compass-clear" onClick={onClear} title="Clear destination">
          ✕
        </button>
      )}
    </div>
  );
};
